// Heists: plan a co-op job, and real members of your mafia fill the other roles.
import { useState } from 'react';
import { api } from '../api.ts';
import { useGame, money, fmt, duration, ago } from '../state.tsx';
import { Loading, useLoad, go, Panel, PlayerLink } from '../components/ui.tsx';
import { Portrait } from '../art/Portrait.tsx';

function pct(x: number) {
  return `${Math.round(x * 100)}%`;
}

export function HeistsPage() {
  const { me, act, content, setFlash } = useGame();
  const { data, reload } = useLoad(() => api.get('/api/heists'), [me?.heistsOpen], 30_000);
  const [planning, setPlanning] = useState<string | null>(null);
  if (!data) return <Loading />;
  const roles = content.heistRoles;
  const defs: Map<string, any> = new Map(content.heists.map((h: any) => [h.id, h]));

  const afterJoin = (r: any) => {
    if (!r) return;
    const res = r.result;
    if (res) {
      const mine = res.shares.find((s: any) => s.playerId === me.id);
      setFlash(res.success
        ? { kind: 'good', title: 'The heist went off without a hitch!', text: <>The crew took <span className="money">{money(res.payout)}</span>. Your cut: <span className="money">{money(mine?.cash ?? 0)}</span> and <span className="xp">{mine?.xp} experience</span>.</> }
        : { kind: 'bad', title: 'The heist went wrong.', text: <>The crew scattered. Everyone lost {content.rules.heistFailDamage} health but learned something.</> });
    } else {
      setFlash({ kind: 'good', text: 'You’re in. The heist happens as soon as the last role is filled.' });
    }
    reload();
  };
  const plan = async (heistId: string, role: string) => {
    const r = await act('/api/heists/plan', { heistId, role });
    if (r) {
      setPlanning(null);
      setFlash({ kind: 'good', text: 'Heist planned! Your mafia has been told. It happens as soon as every role is filled.' });
      reload();
    }
  };
  const join = async (h: any, role: string) => afterJoin(await act('/api/heists/join', { heistRow: h.id, role }));
  const leave = async (h: any) => {
    const organizer = h.organizerId === me.id;
    if (!confirm(organizer ? 'Call off this heist? Everyone gets their energy/stamina back.' : 'Back out of this heist? You get your energy/stamina back.')) return;
    await act(organizer ? '/api/heists/cancel' : '/api/heists/leave', { heistRow: h.id });
    reload();
  };

  const HeistCard = ({ h }: { h: any }) => {
    const def = defs.get(h.heistId);
    const inCrew = h.crew.some((c: any) => c.playerId === me.id);
    return (
      <div className={`heist-card ${h.status}`}>
        <div className="row" style={{ alignItems: 'baseline', flexWrap: 'wrap', gap: 6 }}>
          <div className="job-name grow">{def.name}</div>
          {h.status === 'open' ? <span className="small">Closes in <b>{duration(Math.max(0, h.deadline - Date.now()))}</b></span>
            : h.status === 'success' ? <span className="good small">Success · {money(h.payout)} · {ago(h.resolvedAt)}</span>
            : <span className="bad small">Botched · {ago(h.resolvedAt)}</span>}
        </div>
        <div className="small dim">Planned by <PlayerLink id={h.organizerId} name={h.organizerName} /> · odds right now {pct(h.chance)} · total take about {money(def.payout)}</div>
        <div className="heist-roles">
          {def.roles.map((r: any) => {
            const c = h.crew.find((x: any) => x.role === r.role);
            return (
              <div key={r.role} className={`heist-role ${c ? 'filled' : ''}`}>
                <div className="role-name">{roles[r.role].name}</div>
                {c ? (
                  <>
                    <Portrait seed={c.portrait} playerId={c.playerId} size={36} />
                    <div className="small"><PlayerLink id={c.playerId} name={c.name} /></div>
                    {c.share > 0 && <div className="money small">{money(c.share)}</div>}
                  </>
                ) : h.status === 'open' ? (
                  <>
                    <div className="small dim">{r.cost} {roles[r.role].cost}</div>
                    {h.canJoin && !inCrew && <button className="btn small green" onClick={() => join(h, r.role)}>Join</button>}
                  </>
                ) : <div className="small dim">empty</div>}
              </div>
            );
          })}
        </div>
        {h.status === 'open' && inCrew && <div className="right small"><a onClick={() => leave(h)}>{h.organizerId === me.id ? 'Call it off' : 'Back out'}</a></div>}
      </div>
    );
  };

  return (
    <div>
      <h1 className="page-title">Heists</h1>
      <p className="muted">
        Heists are jobs for a crew of real players. Plan one and pick your role; members of your mafia who are real players get a message and have a few
        hours to fill the other roles. As soon as every role is filled, the heist happens: the take is split evenly, with the planner getting a
        {' '}{content.rules.heistOrganizerShares}× share. If the crew isn’t full in time, it’s called off and everyone gets their energy/stamina back.
        The odds depend on the crew’s levels.
      </p>
      {!data.isHuman && <div className="flash bad"><div>Heists are for real players only.</div></div>}
      {data.open.length > 0 && <Panel title={`Heists being planned (${data.open.length})`}>{data.open.map((h: any) => <HeistCard key={h.id} h={h} />)}</Panel>}

      <Panel title="Plan a heist" right={<span className="small dim">{data.humanMembers} real player{data.humanMembers === 1 ? '' : 's'} in your mafia</span>}>
        {content.heists.map((def: any) => {
          const s = data.heists.find((x: any) => x.heistId === def.id);
          const locked = me.level < def.reqLevel;
          const crewShort = data.humanMembers < def.roles.length - 1;
          return (
            <div key={def.id} className={`heist-plan ${locked ? 'locked' : ''}`}>
              <div className="grow">
                <div className="job-name">{def.name}</div>
                <div className="small muted">{def.description}</div>
                <div className="small">
                  Crew of {def.roles.length}: {def.roles.map((r: any) => `${roles[r.role].name} (${r.cost} ${roles[r.role].cost})`).join(' · ')}
                </div>
                <div className="small">
                  Take <span className="money">~{money(def.payout)}</span> · <span className="xp">{def.xp} XP each</span> · {def.windowHours}h to fill · odds with a crew at your level {pct(s.chanceAtMyLevel)}
                </div>
                {locked && <div className="small"><span className="locked-stamp">Locked</span> until level {def.reqLevel}</div>}
                {!locked && crewShort && <div className="small bad">Needs {def.roles.length - 1} real player{def.roles.length - 1 === 1 ? '' : 's'} in your mafia. <a onClick={() => go('mafia')}>Invite friends</a></div>}
                {s.cooldownMs > 0 && <div className="small dim">You can plan this again in {duration(s.cooldownMs)}.</div>}
                {planning === def.id && (
                  <div className="heist-pick">
                    <span className="small">Your role:</span>
                    {def.roles.map((r: any) => (
                      <button key={r.role} className="btn small" title={roles[r.role].blurb} onClick={() => plan(def.id, r.role)}>{roles[r.role].name} ({r.cost} {roles[r.role].cost})</button>
                    ))}
                    <a className="small" onClick={() => setPlanning(null)}>cancel</a>
                  </div>
                )}
              </div>
              {planning !== def.id && (
                <button className={`btn ${locked || crewShort || s.cooldownMs > 0 || !data.isHuman ? 'disabled' : ''}`} onClick={() => !(locked || crewShort || s.cooldownMs > 0) && setPlanning(def.id)}>Plan it</button>
              )}
            </div>
          );
        })}
      </Panel>

      {data.recent.length > 0 && <Panel title="Your recent heists">{data.recent.map((h: any) => <HeistCard key={h.id} h={h} />)}</Panel>}
      {data.cityWide.length > 0 && (
        <Panel title="Word on the street">
          {data.cityWide.map((h: any) => (
            <div key={h.id} className="feed-item">
              <span className="grow"><PlayerLink id={h.organizerId} name={h.organizerName} />’s crew {h.status === 'success' ? 'pulled off' : 'botched'} “{defs.get(h.heistId).name}”</span>
              {h.status === 'success' ? <span className="money">{money(h.payout)}</span> : <span className="bad small">busted</span>}
              <span className="when">{ago(h.resolvedAt)}</span>
            </div>
          ))}
        </Panel>
      )}
      <div className="small dim">Heists pulled off: {fmt(me.heistsDone ?? 0)}</div>
    </div>
  );
}
